/* AtomiQ header (v4.1): follows the ground under it (graphite or bright glass), shows how far you have read,
   marks the chapter you are in, and scrolls to anchors with its own height kept clear.
   Round 3: nothing runs on the scroll path where the browser can do it. The reading line and the fade under a
   stuck bar are scroll-driven CSS (section-header.css); a browser without scroll timelines gets one shared rAF job
   (AQ.onScroll). The chapter you are in comes from an IntersectionObserver on a line at 40 % of the screen.
   A chapter link whose section is on this page (the product page has "Hoe het werkt", "Gebruik" and "Vragen")
   jumps within the page; one whose section is not here goes to the home page and never gets the active dot.
   The menu dialog itself is opened by theme.js (data-aq-dialog-open). */

const reducedQuery = matchMedia('(prefers-reduced-motion: reduce)');
const cssScroll = Boolean(window.CSS?.supports?.('animation-timeline: scroll()'));

class AqHeader extends HTMLElement {
  connectedCallback() {
    this.links = [...this.querySelectorAll('[data-nav]')];
    this.current = undefined;
    this.onRefresh = () => this.collect();
    this.onAnchor = (event) => this.anchor(event);
    document.addEventListener('click', this.onAnchor);
    document.addEventListener('shopify:section:load', this.onRefresh);
    document.addEventListener('shopify:section:unload', this.onRefresh);
    this.collect();
    if (!cssScroll) this.stopScroll = this.fallback();
    /* The line under the bar once it is stuck (the announcement has scrolled away). */
    this.stopGround = window.AQ?.followGround?.(this, () => this.offsetHeight + 1) || null;
    if (document.readyState === 'complete') this.arrive();
    else addEventListener('load', () => this.arrive(), { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onAnchor);
    document.removeEventListener('shopify:section:load', this.onRefresh);
    document.removeEventListener('shopify:section:unload', this.onRefresh);
    this.spy?.disconnect();
    this.stopScroll?.();
    this.stopGround?.();
  }

  /* Without scroll timelines: --read and .is-stuck from one read of scrollY per frame (the shared scroll job). */
  fallback() {
    const doc = document.documentElement;
    let stuck = null;
    const read = (y) => {
      const max = doc.scrollHeight - innerHeight;
      const announce = parseFloat(getComputedStyle(doc).getPropertyValue('--announce-h')) || 0;
      return { read: max > 0 ? Math.min(1, y / max) : 0, stuck: y > announce };
    };
    const write = ({ read: value, stuck: isStuck }) => {
      this.style.setProperty('--read', value.toFixed(4));
      if (isStuck !== stuck) {
        stuck = isStuck;
        this.classList.toggle('is-stuck', isStuck);
      }
    };
    if (window.AQ?.onScroll) return window.AQ.onScroll(read, write);
    const tick = () => write(read(scrollY));
    addEventListener('scroll', tick, { passive: true });
    tick();
    return () => removeEventListener('scroll', tick);
  }

  /* Chapters: sections that say which chapter they belong to (data-chapter), and the anchors the links point at.
     Every chapter link on the page (bar and menu) jumps within the page when its section is here. */
  collect() {
    const found = new Map();
    document.querySelectorAll('[data-chapter]').forEach((el) => found.set(el, el.dataset.chapter));
    this.local = new Set();
    document.querySelectorAll('a[data-nav]').forEach((link) => {
      const key = link.dataset.nav;
      if (!link.dataset.href) link.dataset.href = link.getAttribute('href') || '';
      const el = this.target(key);
      if (el) {
        this.local.add(key);
        link.setAttribute('href', `#${key}`);
        if (!found.has(el)) found.set(el, key);
      } else {
        link.setAttribute('href', link.dataset.href);
      }
    });
    this.chapters = [...found].map(([el, key]) => ({ el, key }));
    this.watch();
  }

  /* The chapter under a line at 40 % of the screen: the last one in page order that crosses it. */
  watch() {
    this.spy?.disconnect();
    this.inView = new Set();
    if (!('IntersectionObserver' in window) || !this.chapters.length) {
      this.mark(null);
      return;
    }
    const keyOf = new Map(this.chapters.map(({ el, key }) => [el, key]));
    this.spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? this.inView.add(entry.target) : this.inView.delete(entry.target)));
        let pick = null;
        for (const el of this.inView) if (!pick || pick.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) pick = el;
        this.mark(pick ? keyOf.get(pick) : null);
      },
      { rootMargin: '-40% 0px -59% 0px' }
    );
    this.chapters.forEach(({ el }) => this.spy.observe(el));
  }

  mark(key) {
    /* A chapter that lives on another page never gets the active dot here. */
    const current = key && this.local?.has(key) ? key : null;
    if (current === this.current) return;
    this.current = current;
    this.links.forEach((link) => {
      if (link.dataset.nav === current) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  target(name) {
    if (!name) return null;
    return (
      document.getElementById(name) ||
      document.querySelector(`[data-anchor="${CSS.escape(name)}"]`) ||
      document.querySelector(`.shopify-section[id$="__${CSS.escape(name)}"]`)
    );
  }

  /* Where the page should stop: a section's heading just under the bar, not its empty top padding. */
  stopFor(el) {
    const top = el.getBoundingClientRect().top + scrollY;
    const bar = this.offsetHeight;
    let inset = 0;
    if (el.matches('[data-ground]')) {
      const padding = parseFloat(getComputedStyle(el).paddingTop) || 0;
      inset = Math.max(0, padding - 32);
    }
    return Math.max(0, top + inset - bar);
  }

  scrollToEl(el, smooth = true) {
    const still = !smooth || reducedQuery.matches || document.documentElement.classList.contains('is-motion-paused');
    window.scrollTo({ top: this.stopFor(el), behavior: still ? 'auto' : 'smooth' });
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }

  anchor(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href*="#"]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.hash.length < 2) return;
    const el = this.target(decodeURIComponent(url.hash.slice(1)));
    if (!el) return;
    event.preventDefault();
    if (url.hash !== window.location.hash) history.pushState(null, '', url.hash);
    /* A link in the menu dialog: theme.js has just closed it; scroll once the page can scroll again. */
    requestAnimationFrame(() => this.scrollToEl(el));
  }

  /* Arriving from another page with an anchor (/#formules): land on the heading, like a click would. */
  arrive() {
    const hash = window.location.hash;
    if (hash.length < 2) return;
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav && nav.type !== 'navigate') return;
    const el = this.target(decodeURIComponent(hash.slice(1)));
    if (el) requestAnimationFrame(() => this.scrollToEl(el, false));
  }
}

if (!customElements.get('aq-header')) customElements.define('aq-header', AqHeader);

/* The announcement (round v4.4): a mouse coming over the bar, or a tap on it, sends the van past once more, in front
   of the line (data-again hides the cover). Only once the drive before it is over, and never where nothing moves
   (reduced motion, the motion switch, announce_motion off: the drive is not rendered, so it has no animations).
   The first drive itself is CSS (section-header.css) and needs none of this. */
class AqAnnounce extends HTMLElement {
  connectedCallback() {
    this.onReplay = (event) => {
      if (event.type === 'pointerenter' && event.pointerType !== 'mouse') return;
      this.replay();
    };
    this.addEventListener('pointerenter', this.onReplay);
    this.addEventListener('click', this.onReplay);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.onReplay);
    this.removeEventListener('click', this.onReplay);
  }

  replay() {
    const drive = this.querySelector('.announce__drive');
    const anims = drive?.getAnimations?.({ subtree: true }) || [];
    if (!anims.length || anims.some((anim) => anim.playState === 'running')) return;
    this.setAttribute('data-again', '');
    anims.forEach((anim) => {
      anim.currentTime = anim.effect?.getTiming().delay || 0;
      anim.play();
    });
  }
}

if (!customElements.get('aq-announce')) customElements.define('aq-announce', AqAnnounce);
